import {AppBar, Box, IconButton, Toolbar, Typography} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import {useLocation} from 'react-router-dom'
import {HEADER_HEIGHT_DESKTOP, HEADER_HEIGHT_MOBILE} from 'src/theme-new/tokens'
import {isPathActive} from './isPathActive'
import {NavItemConfig} from './types'
import UserMenu from './UserMenu'
import {useAppSelector} from 'src/hooks'
import {PATHS} from 'src/constants/constants'
import StatusPill from 'src/components-new/primitives/StatusPill'

interface HeaderBarProps {
  onMenuClick?: () => void
  navItems?: NavItemConfig[]
}

function parseStates(state: any): string {
  switch (state) {
    case 'TO_ACTIVE': return 'Da attivare'
    case 'ACTIVATING': return 'In attivazione'
    case 'ACTIVE': return 'Attivo'
    case 'ACTIVE_DELIVERY': return 'Attivo delivery'
    case 'SUSPENDED': return 'Sospeso'
    case 'WAITING_TO_EXIT': return 'In attesa di uscita'
    case 'EXITED': return 'Drop-out'
    default: return '-'
  }
}

function getStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (!state) return 'default'
  if (state === 'ACTIVE' || state === 'ACTIVE_DELIVERY') return 'success'
  if (state === 'SUSPENDED') return 'warning'
  if (state === 'EXITED') return 'error'
  return 'default'
}

const HeaderBar = ({onMenuClick, navItems}: HeaderBarProps): JSX.Element => {
  const location = useLocation()
  const userInfoData = useAppSelector(state => state.userReducer.data)

  const isProfilePage = isPathActive(location.pathname, PATHS.PROFILE)

  const activeItem = navItems?.find(item => isPathActive(location.pathname, item.path))
  const pageTitle = activeItem?.label ?? ''

  const profileName = isProfilePage
    ? `${userInfoData?.firstName ?? ''} ${userInfoData?.lastName ?? ''}`.trim() || '—'
    : null

  const titleContent = isProfilePage ? (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
          color: 'text.primary',
          letterSpacing: '0.15px',
        }}
      >
        {profileName}
      </Typography>
      <StatusPill
        label={parseStates(userInfoData?.state)}
        color={getStateColor(userInfoData?.state)}
      />
    </Box>
  ) : (
    <Typography
      sx={{
        fontSize: 20,
        fontWeight: 700,
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        color: 'text.primary',
        letterSpacing: '0.15px',
      }}
    >
      {pageTitle}
    </Typography>
  )

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          display: {xs: 'flex', md: 'none'},
          height: HEADER_HEIGHT_MOBILE,
          borderBottom: '1px solid',
          borderColor: 'divider',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Toolbar sx={{minHeight: `${HEADER_HEIGHT_MOBILE}px !important`, px: 1, gap: 1}}>
          <IconButton
            aria-label="Apri menu"
            onClick={onMenuClick}
            size="medium"
          >
            <MenuIcon />
          </IconButton>
          {isProfilePage ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, ml: 0.5}}>
              <Typography sx={{fontSize: 16, fontWeight: 700, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: 'text.primary'}}>
                {profileName}
              </Typography>
              <StatusPill label={parseStates(userInfoData?.state)} color={getStateColor(userInfoData?.state)} />
            </Box>
          ) : (
            <Typography sx={{fontSize: 16, fontWeight: 700, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: 'text.primary', ml: 0.5}}>
              {pageTitle}
            </Typography>
          )}
          <Box sx={{flex: 1}} />
          <UserMenu expanded={false} />
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: {xs: 'none', md: 'flex'},
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          height: HEADER_HEIGHT_DESKTOP,
          flexShrink: 0,
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {titleContent}
        <UserMenu expanded={true} />
      </Box>
    </>
  )
}

export default HeaderBar
